/* eslint-disable react/jsx-handler-names */
import React, { useEffect, useMemo, useRef } from 'react'
import { useForm, Controller, FormProvider, FieldErrors } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { getFormData, getLoadingState, IMedicareComplaintAction } from 'redux/reducers/medicare-complaint-form'

import { MyChoiceList } from 'components/ReactHookForm/MyChoiceList'
import { getErrorMessageRegister, getClassNames } from 'utils/react-hook-form'
import { Button } from 'components/Button/Button'
import Cleave from 'cleave.js/react'
import { getUserData } from 'redux/reducers/users'
import { useTranslation } from 'react-i18next'
import { MyTextField } from 'components/ReactHookForm/MyTextField'
import { fieldRuleGenerator } from '../validation/field-rules'
import { buildAbsoluteMedicareMyUrl } from 'utils/urls/buildAbsoluteMedicareMyUrl'
import { MySpinner } from 'components/MySpinner/MySpinner'

export interface Step2Props {
  handleOnSubmit: (data: any) => void;
  handleOnBack: (data: any) => void;
  handleOnError: (data: any) => void;
  handleOnPreventUserStep: () => void;
}

export const Step2: React.FC<Step2Props> = ({ handleOnSubmit, handleOnError, handleOnBack, handleOnPreventUserStep }) => {
  const formData = useSelector(getFormData)
  const userData = useSelector(getUserData)

  const blastMaskingClass = 'qm-block-pii'

  const defaultValuesObject = useMemo(() => {
    return ({
      emailAddress: formData.emailAddress,
      preferredLanguage: userData.currentLanguagePreference,
      preferredCallbackNumber: formData.preferredCallbackNumber,
      preferredCallbackTime: formData.preferredCallbackTime,
      issueConcerningPlan: formData.issueConcerningPlan,
      noteIssues: formData.noteIssues,
      planName: formData.planName,
      planContractId: formData.planContractId
    })
  }, [userData, formData])

  const methods = useForm<IMedicareComplaintAction>({
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues: defaultValuesObject,
    shouldFocusError: false
  })
  const { handleSubmit, control, reset, formState } = methods
  const { errors } = formState

  useEffect(() => {
    reset(defaultValuesObject as any, {
      keepErrors: true
    })
  }, [reset, defaultValuesObject])

  useEffect(() => {
    if (!(formData.mbi && formData.coverageStartDate && formData.lastName && formData.dateOfBirth && formData.zipCode)) {
      handleOnPreventUserStep()
    }
  }, [formData, handleOnPreventUserStep])

  const loadingState = useSelector(getLoadingState)

  const ref = useRef<HTMLAnchorElement | HTMLButtonElement>(null)

  const { t } = useTranslation('mcfStep2Page')

  const alertRef = useRef<HTMLDivElement | null>(null)
  const handleOnFormError = (data: FieldErrors) => {
    handleOnError(data)
    alertRef.current?.focus() // only works in chrome if user tabs and enters the submit buttom via keyboard
  }

  // If API request fails, focus on errors
  useEffect(() => {
    if (loadingState === 'error') {
      alertRef.current?.focus()
    }
  }, [loadingState])

  return (
    <FormProvider {...methods}>
      <form data-testid='form-step2' onSubmit={handleSubmit(handleOnSubmit, handleOnFormError)}>
        <div className='ds-l-form-row'>
          <div className='ds-l-col--12'>
            <h1 className='ds-text-heading--3xl my-c-main-header-margin' id='maincontent' tabIndex={-1}>{t('Medicare Complaint Form')}</h1>
            <h2 className='ds-text-heading--sm my-u-font-size--18 my-u-mb--6'>{t('Step 2 of 3')}</h2>
            <h2 className='ds-text-heading--2xl my-u-mt--0 my-u-mb--5'>{t('Your contact information')}</h2>
            <span className='my-u-mb--6 ds-u-display--block required-text ds-u-font-size--sm'>{t('All fields are required')}</span>
          </div>
        </div>
        {/* eslint-disable-next-line operator-linebreak */}
        <div ref={alertRef} tabIndex={-1}>
          {formState.isSubmitted && (loadingState === 'error' || Object.keys(errors).length > 0) &&
            (
              <div className='ds-c-alert ds-c-alert--error ds-c-alert--hide-icon' data-testid='step2-alert-error'>
                <div className='ds-c-alert__body'>
                  <p className='ds-c-alert__text'>
                    {loadingState === 'error' &&
                      <>
                        <h2 className='ds-c-alert__heading'>Error</h2>
                        {t('step2Error.text')}
                      </>}
                    {Object.keys(errors).length > 0 &&
                      <>
                        <h2 className='ds-c-alert__heading'>{t('Error Complete these items to verify your Medicare information')}</h2>
                        <ul className='modal-blue'>
                          {errors.emailAddress?.message && <li><a href='#emailAddress'>{t('EMAIL ADDRESS')}</a>{`: ${errors.emailAddress.message}`}</li>}
                          {errors.preferredLanguage?.message && <li><a href='#preferredLanguage'>{t('Preferred language')}</a>{`: ${errors.preferredLanguage.message}`}</li>}
                          {errors.preferredCallbackNumber?.message && <li><a href='#preferredCallbackNumber'>{t('Preferred call back number')}</a>{`: ${errors.preferredCallbackNumber.message}`}</li>}
                          {errors.preferredCallbackTime?.message && <li><a href='#preferredCallbackTime'>{t('Preferred call back time')}</a>{`: ${errors.preferredCallbackTime.message}`}</li>}
                          {errors.issueConcerningPlan?.message && <li><a href='#issueConcerningPlan'>{errors.issueConcerningPlan.message}</a></li>}
                          {errors.planName?.message && <li><a href='#planName'>{t('PLAN NAME')}</a>{`: ${errors.planName.message}`}</li>}
                          {errors.planContractId?.message && <li><a href='#planContractId'>{t('PLAN CONTRACT ID')}</a>{`: ${errors.planContractId.message}`}</li>}
                          {errors.noteIssues?.message && <li><a href='#noteIssues'>{t('DESCRIPTION OF COMPLAINT')}</a>{`: ${errors.noteIssues.message}`}</li>}
                        </ul>
                      </>}
                  </p>
                </div>
              </div>
            )}
        </div>
        {/* eslint-disable-next-line no-use-before-define */}
        <div className='ds-l-form-row my-u-mt--10 my-c-text-field-margin'>
          <div className='ds-l-md-col--12'>
            <MyTextField
              name='emailAddress'
              className={blastMaskingClass}
              rules={fieldRuleGenerator.emailAddress(t)}
              label={t('EMAIL ADDRESS')}
              data-testid='emailAddress'
            />
          </div>
        </div>
        <div className='ds-l-form-row my-c-text-field-margin'>
          <div className='ds-l-col--auto'>
            <MyChoiceList
              inputClassName={blastMaskingClass}
              variableInputName='preferredLanguage'
              label={t('Preferred language')}
              choices={[
                {
                  label: t('English'),
                  value: 'en'
                },
                {
                  label: t('Spanish'),
                  value: 'es'
                }
              ]}
              errorMessageString={t('Error Select your preferred language')}
            />
          </div>
        </div>
        <div className='ds-l-form-row my-c-text-field-margin'>
          <Controller
            rules={fieldRuleGenerator.callbackNumber(t)}
            render={({ field, fieldState }) => {
              return (
                <div className='ds-l-col--auto ds-l-md-col--5'>
                  <label className='ds-c-label my-u-font-size--14 preferredNumber' htmlFor='preferredCallbackNumber'>{t('Preferred call back number')}</label>
                  <Cleave
                    data-testid='preferredCallbackNumber'
                    id='preferredCallbackNumber'
                    options={{ numericOnly: true, blocks: [0, 3, 3, 4], delimiters: ['(', ')', '-'] }}
                    className={`${blastMaskingClass} ` + getClassNames(`ds-c-field`, Boolean(fieldState?.error?.message), `ds-c-field--error`)}
                    name={field.name}
                    type='text'
                    onChange={(e) => {
                      field.onChange(e.target.rawValue)
                    }}
                    onBlur={field.onBlur}
                    value={field.value}
                    aria-required
                    aria-invalid={!!fieldState.error}
                  />
                  {getErrorMessageRegister(Boolean(fieldState?.error?.message), fieldState.error?.message ?? '', 'error-message')}
                </div>
              )
            }}
            name='preferredCallbackNumber'
            control={control}
            defaultValue=''
          />
        </div>
        <div className='ds-l-form-row ds-u-margin-bottom--7'>
          <div className='ds-l-col--auto'>
            <MyChoiceList
              variableInputName='preferredCallbackTime'
              label={t('Preferred call back time')}
              inputClassName={`${blastMaskingClass}`}
              choices={[
                {
                  label: t('callback-time-labels.morning'),
                  value: 'AM'
                },
                {
                  label: t('callback-time-labels.evening'),
                  value: 'PM'
                },
                {
                  label: t('callback-time-labels.any'),
                  value: 'ANY'
                }
              ]}
              errorMessageString={t('Error Select your preferred call back time')}
            />
          </div>
        </div>
        <div className='ds-l-form-row my-u-mb--20'>
          <div className='ds-l-col--12'>
            <h2 className='ds-text-heading--2xl my-u-mb--15'>{t('Your complaint information')}</h2>
            <span className='my-u-mt--5 required-text ds-u-font-size--sm'>{t('All fields are required')}</span>
          </div>
        </div>
        <div className='ds-l-form-row my-c-text-field-margin'>
          <div className='ds-l-col--12'>
            <MyChoiceList
              variableInputName='issueConcerningPlan'
              label={t('Is your complaint about access to your benefits, drugs, or services?')}
              inputClassName={`${blastMaskingClass}`}
              choices={[
                {
                  label: t('Yes'),
                  value: 'YES'
                },
                {
                  label: t('No'),
                  value: 'NO'
                }
              ]}
              errorMessageString={t('Error Select if your complaint about access to your benefits, drugs, or services')}
            />
          </div>
        </div>
        <div className='ds-l-form-row my-c-text-field-margin'>
          <div className='ds-l-col--12'>
            <h3 className='ds-text-heading--xl'>{t('Which plan are you filing this complaint about?')}</h3>
            <MyTextField
              name='planName'
              fieldClassName={`${blastMaskingClass}`}
              rules={fieldRuleGenerator.planName(t)}
              label={t('PLAN NAME')}
              data-testid='planName'
            />
          </div>
        </div>
        <div className='ds-l-form-row my-c-text-field-margin'>
          <div className='ds-l-col--12'>
            <MyTextField
              name='planContractId'
              fieldClassName={`${blastMaskingClass}`}
              rules={fieldRuleGenerator.planContractId(t)}
              label={<div><p className='my-u-mb--0'>{t('PLAN CONTRACT ID')}</p><p><span className='my-c-required-text-field'>{t('All fields are required')}</span></p></div>}
              ariaLabel={t('PLAN CONTRACT ID')}
              data-testid='planContractId'
            />
          </div>
        </div>
        <div className='ds-l-form-row my-c-text-field-margin'>
          <div className='ds-l-col--12'>
            {/* TODO: This should be refactored so 'All fiedls are required' is not built into this label  */}
            <MyTextField
              rules={fieldRuleGenerator.descriptionOfComplaint(t)}
              name='noteIssues'
              label={t('DESCRIPTION OF COMPLAINT')}
              defaultValue=''
              data-testid='noteIssues'
              multiline
              fieldClassName={`${blastMaskingClass} my-c-textarea`}
            />
          </div>
        </div>
        <Button
          data-testid='step2-back'
          ref={ref}
          className='ds-c-button my-u-mr--10'
          onClick={handleOnBack}
          siteActivityProps={{
            component: 'MCF',
            clickData: {
              linkText: 'Back',
              linkUrl: buildAbsoluteMedicareMyUrl('/medicare-complaint/step1')
            }
          }}
        >
          {t('Back')}
        </Button>
        <Button
          ref={ref}
          className='ds-c-button ds-c-button--solid my-c-spinner-button'
          data-testid='step2-next'
          type='submit'
          siteActivityProps={{
            component: 'MCF',
            clickData: {
              linkText: 'Submit Complaint',
              linkUrl: buildAbsoluteMedicareMyUrl('/medicare-complaint/success')
            }
          }}
        >
          {loadingState === 'loading' && <MySpinner size='small' inverse isInButton/>}
          {t('Submit')}
        </Button>
      </form>
    </FormProvider>
  )
}
